import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { fetchDashboardStats, type DashboardStats, type PeriodDays } from "../api/dashboard";
import { format, parseISO } from "date-fns";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDateTick(dateStr: string, periodDays: PeriodDays) {
  try {
    const d = parseISO(dateStr);
    return periodDays <= 7 ? format(d, "EEE d") : format(d, "MMM d");
  } catch {
    return dateStr;
  }
}

// ─── Change badge ─────────────────────────────────────────────────────────────

function ChangeBadge({ pct, dark = false }: { pct: number | null; dark?: boolean }) {
  if (pct === null) return null;
  const up = pct >= 0;
  const color = up ? "#22C55E" : "#EF4444";
  const bg = up ? (dark ? "rgba(34,197,94,0.15)" : "#F0FDF4") : dark ? "rgba(239,68,68,0.15)" : "#FEF2F2";
  const Icon = up ? TrendingUpRoundedIcon : TrendingDownRoundedIcon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        backgroundColor: bg,
        borderRadius: "6px",
        px: 0.8,
        py: 0.3,
      }}
    >
      <Icon sx={{ fontSize: 13, color }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color }}>
        {Math.abs(pct)}%
      </Typography>
    </Box>
  );
}

// ─── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  changePct: number | null;
  dark?: boolean;
  loading: boolean;
  comingSoon?: boolean;
}

function StatCard({ label, value, changePct, dark = false, loading, comingSoon = false }: StatCardProps) {
  return (
    <Paper
      elevation={0}
      aria-disabled={comingSoon || undefined}
      sx={{
        p: 3,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: comingSoon ? "divider" : dark ? "transparent" : "#E2E8F0",
        backgroundColor: comingSoon ? "action.disabledBackground" : dark ? "#0F172A" : "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: "100%",
      }}
    >
      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: comingSoon ? "text.disabled" : dark ? "rgba(255,255,255,0.55)" : "#64748B" }}>
        {label}
      </Typography>
      {comingSoon ? (
        <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.disabled" }}>
          Coming soon
        </Typography>
      ) : loading ? (
        <>
          <Skeleton variant="text" width="60%" height={40} sx={{ bgcolor: dark ? "rgba(255,255,255,0.08)" : undefined }} />
          <Skeleton variant="text" width="40%" height={20} sx={{ bgcolor: dark ? "rgba(255,255,255,0.08)" : undefined }} />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: dark ? "#D4A63D" : "#0F172A", lineHeight: 1 }}>
            {value}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ChangeBadge pct={changePct} dark={dark} />
            <Typography sx={{ fontSize: "0.72rem", color: dark ? "rgba(255,255,255,0.35)" : "#94A3B8" }}>
              vs prev period
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
}

function ImpactMetric({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <Box sx={{ py: 1.2, borderBottom: "1px solid #F1F5F9", "&:last-child": { borderBottom: 0 } }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#64748B", mb: 0.4 }}>{label}</Typography>
      {loading ? (
        <Skeleton variant="text" width="40%" />
      ) : (
        <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>{value}</Typography>
      )}
    </Box>
  );
}

// ─── Custom tooltip ─────────────────────────────────────────────────────────────

const ChartTooltip = ({ active, payload, label, currency }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: "#0F172A",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: "10px",
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mb: 0.5 }}>{label}</Typography>
      {payload.map((entry: any) => (
        <Box key={entry.dataKey} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ fontSize: "0.78rem", color: entry.color }}>{entry.name}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#fff", fontWeight: 600 }}>
            {entry.dataKey === "revenue"
              ? formatCurrency(entry.value, currency)
              : entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Overview() {
  const [period, setPeriod] = useState<PeriodDays>(30);
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDashboardStats(period)
      .then(setData)
      .catch((e) => {
        console.error("Dashboard fetch failed:", e);
        setError("Could not load dashboard data.");
      })
      .finally(() => setLoading(false));
  }, [period]);

  const currency = data?.currency ?? "USD";
  const s = data?.stats;
  const aiRevenue = s?.ai_assisted_revenue ?? s?.revenue;
  const impact = data?.setin_impact;
  const chartData = (data?.chart_data ?? []).map((d) => ({
    ...d,
    label: formatDateTick(d.date, period),
  }));

  const hasData = chartData.some((d) => d.conversations > 0 || d.bookings > 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <GridViewRoundedIcon sx={{ color: "#C9734F", fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0F172A" }}>
            Overview
          </Typography>
        </Box>

        {/* Period toggle */}
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v) => v && setPeriod(v as PeriodDays)}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              border: "1px solid #E2E8F0",
              color: "#64748B",
              fontWeight: 600,
              fontSize: "0.78rem",
              textTransform: "none",
              px: 2,
              py: 0.6,
              "&.Mui-selected": {
                backgroundColor: "#C9734F",
                color: "#fff",
                borderColor: "#C9734F",
                "&:hover": { backgroundColor: "#b8613f" },
              },
              "&:hover": { backgroundColor: "#F8FAFC" },
            },
          }}
        >
          <ToggleButton value={7}>7d</ToggleButton>
          <ToggleButton value={30}>30d</ToggleButton>
          <ToggleButton value={90}>90d</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Total Revenue"
            value={s ? formatCurrency(s.total_revenue.current, currency) : "—"}
            changePct={s?.total_revenue.change_pct ?? null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Bookings"
            value={s ? String(s.bookings.current) : "—"}
            changePct={s?.bookings.change_pct ?? null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="AI-Assisted Revenue"
            value={aiRevenue ? formatCurrency(aiRevenue.current, currency) : "—"}
            changePct={aiRevenue?.change_pct ?? null}
            dark
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="AI-Assisted Bookings"
            value={s?.ai_assisted_bookings ? String(s.ai_assisted_bookings.current) : "—"}
            changePct={s?.ai_assisted_bookings?.change_pct ?? null}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Second stat row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Conversations"
            value={s ? String(s.conversations.current) : "—"}
            changePct={s?.conversations.change_pct ?? null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Conversion Rate"
            value={s ? `${s.conversion_rate.current}%` : "—"}
            changePct={s?.conversion_rate.change_pct ?? null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Average Booking Value"
            value={s?.average_booking_value ? formatCurrency(s.average_booking_value.current, currency) : "—"}
            changePct={s?.average_booking_value?.change_pct ?? null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            label="Missed Opportunities"
            value=""
            changePct={null}
            loading={loading}
            comingSoon
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <StatCard
            label="Direct Bookings (Booking Page)"
            value={s ? String(s.direct_bookings.current) : "—"}
            changePct={s?.direct_bookings.change_pct ?? null}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      {error ? (
        <Paper elevation={0} sx={{ p: 4, borderRadius: "16px", border: "1px solid #FEE2E2", backgroundColor: "#FFF5F5", textAlign: "center" }}>
          <Typography sx={{ color: "#EF4444", fontWeight: 500 }}>{error}</Typography>
        </Paper>
      ) : !loading && !hasData ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#fff", textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, color: "#64748B", mb: 1 }}>No activity in this period</Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8" }}>
            Analytics data will appear here once your AI assistant starts handling conversations.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {/* Conversations area chart */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#fff" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A", mb: 2.5 }}>
                Conversations
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "8px" }} />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="convGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip currency={currency} />} />
                    <Area type="monotone" dataKey="conversations" name="Conversations" stroke="#3B82F6" strokeWidth={2} fill="url(#convGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Bookings + Revenue bar chart */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#fff" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#0F172A", mb: 2.5 }}>
                Bookings & Revenue
              </Typography>
              {loading ? (
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "8px" }} />
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barSize={period === 90 ? 4 : period === 30 ? 8 : 16}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip currency={currency} />} />
                    <Bar dataKey="bookings" name="Bookings" fill="#C9734F" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="revenue" name="Revenue" fill="#D4A63D" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {!error && (
        <Grid container spacing={2.5} sx={{ mt: 0 }}>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid #E2E8F0", backgroundColor: "#fff", height: "100%" }}>
              <Typography sx={{ fontWeight: 700, color: "#0F172A", mb: 2 }}>Setin Impact</Typography>
              <ImpactMetric
                label="Bookings Setin helped create"
                value={String(impact?.bookings_helped_create ?? s?.ai_assisted_bookings?.current ?? 0)}
                loading={loading}
              />
              <ImpactMetric
                label="Revenue captured by Setin"
                value={formatCurrency(impact?.revenue_captured ?? aiRevenue?.current ?? 0, currency)}
                loading={loading}
              />
              {impact?.after_hours_conversations !== null && impact?.after_hours_conversations !== undefined && (
                <ImpactMetric
                  label="After-hours conversations"
                  value={`${impact.after_hours_conversations}${impact.after_hours_bookings ? ` conversations / ${impact.after_hours_bookings} bookings` : ""}`}
                  loading={loading}
                />
              )}
              {impact?.calls_handled !== null && impact?.calls_handled !== undefined && (
                <ImpactMetric
                  label="Calls handled"
                  value={`${impact.calls_handled}${impact.calls_created_bookings ? ` calls / ${impact.calls_created_bookings} bookings` : ""}`}
                  loading={loading}
                />
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              aria-disabled="true"
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "action.disabledBackground",
                height: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "text.disabled" }}>Needs Attention</Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.disabled" }}>
                  Coming soon
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.85rem", color: "text.disabled" }}>
                Recoverable booking opportunities will appear here.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
